import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { FiEdit2, FiShare2, FiMail, FiPhone, FiGlobe, FiMapPin, FiSave, FiX, FiGrid, FiUpload, FiLinkedin, FiTwitter, FiFacebook, FiInstagram, FiGithub, FiBriefcase, FiSmartphone, FiUser } from 'react-icons/fi';
import { FaLinkedinIn, FaFacebookF, FaInstagram, FaGithub } from 'react-icons/fa';
import { QRCodeSVG } from 'qrcode.react';
import './MyCard.css';

const MyCard = () => {
  const { user } = useAuth();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState({
    fullName: '',
    jobTitle: '',
    businessName: '',
    email: '',
    phoneNumber: '',
    cellphone: '',
    whatsapp: '',
    wechat: '',
    website: '',
    address: '',
    templateStyle: 'omni',
    businessLogo: null,
    socialLinks: {
      linkedin: '',
      twitter: '',
      facebook: '',
      instagram: '',
      github: '',
      tiktok: '',
      whatsapp: '',
      wechat: '',
    },
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [omniUrl, setOmniUrl] = useState('');
  const [originalSlug, setOriginalSlug] = useState('');
  const [slugStatus, setSlugStatus] = useState(null); // null | 'checking' | 'available' | 'taken' | 'invalid'
  const [slugLastChangedAt, setSlugLastChangedAt] = useState(null);
  const [showSlugModal, setShowSlugModal] = useState(false);
  const [showCooldownPopup, setShowCooldownPopup] = useState(false);
  const slugTimer = React.useRef(null);

  const slugDaysLeft = React.useMemo(() => {
    if (!slugLastChangedAt) return 0;
    const daysSince = (Date.now() - new Date(slugLastChangedAt).getTime()) / (1000 * 60 * 60 * 24);
    return daysSince < 30 ? Math.ceil(30 - daysSince) : 0;
  }, [slugLastChangedAt]);
  const slugLocked = slugDaysLeft > 0;

  useEffect(() => {
    if (user) {
      loadCard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadCard = async () => {
    try {
      setLoading(true);
      const response = await apiService.getMyCard();
      
      if (response.ok && response.card) {
        const cardData = response.card;
        setCard(cardData);
        // Parse social links safely
        let parsedSocialLinks = {
          linkedin: '',
          twitter: '',
          facebook: '',
          instagram: '',
          github: '',
          tiktok: '',
          whatsapp: '',
          wechat: '',
        };
        
        if (cardData.social_links) {
          try {
            if (typeof cardData.social_links === 'string') {
              parsedSocialLinks = JSON.parse(cardData.social_links);
            } else if (typeof cardData.social_links === 'object') {
              parsedSocialLinks = cardData.social_links;
            }
          } catch (e) {
            console.error('Error parsing social links:', e);
          }
        }

        const logoUrl = cardData.business_logo_url;
        
        setFormData({
          fullName: cardData.full_name || '',
          jobTitle: cardData.job_title || '',
          businessName: cardData.business_name || '',
          email: cardData.email || '',
          phoneNumber: cardData.phone_number || '',
          cellphone: cardData.cellphone || '',
          whatsapp: cardData.whatsapp || '',
          wechat: cardData.wechat || '',
          website: cardData.website || '',
          address: cardData.address || '',
          templateStyle: cardData.template_style || 'omni',
          businessLogo: logoUrl || null,
          socialLinks: { linkedin: '', twitter: '', facebook: '', instagram: '', github: '', tiktok: '', whatsapp: '', wechat: '', ...parsedSocialLinks },
        });
        const slug = cardData.public_url_slug || '';
        setOmniUrl(slug);
        setOriginalSlug(slug);
        setSlugLastChangedAt(cardData.slug_last_changed_at || null);
        
        // Set logo preview if exists
        if (logoUrl) {
          // Ensure the URL is absolute
          let absoluteLogoUrl;
          if (logoUrl.startsWith('http')) {
            absoluteLogoUrl = logoUrl;
          } else if (logoUrl.startsWith('/')) {
            // Relative URL starting with /
            absoluteLogoUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${logoUrl}`;
          } else {
            // Relative URL without /
            absoluteLogoUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/${logoUrl}`;
          }
          
          setLogoPreview(absoluteLogoUrl);
          setFormData(prev => ({ ...prev, businessLogo: absoluteLogoUrl }));
          console.log('🖼️ Logo URL from DB:', logoUrl);
          console.log('🌐 Absolute logo URL:', absoluteLogoUrl);
        } else {
          setLogoPreview(null);
          console.log('❌ No logo URL in database');
        }
      }
    } catch (error) {
      console.error('Error loading card:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please select an image file' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image size must be less than 5MB' });
      return;
    }

    setLogoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSlugChange = (value) => {
    const slug = value.toLowerCase();
    setOmniUrl(slug);
    if (!slug || slug === originalSlug) { setSlugStatus(null); return; }

    // Validation rules
    if (slug.length < 6 || slug.length > 24) {
      setSlugStatus('invalid');
      return;
    }

    // Check for invalid characters (only allow letters and numbers)
    if (/[^a-z0-9]/.test(slug)) {
      setSlugStatus('invalid');
      return;
    }

    if (/^\d+$/.test(slug)) {
      setSlugStatus('invalid');
      return;
    }

    setSlugStatus('checking');
    clearTimeout(slugTimer.current);
    slugTimer.current = setTimeout(async () => {
      try {
        const result = await apiService.checkSlug(slug);
        if (result.available) setSlugStatus(result.isSelf ? null : 'available');
        else setSlugStatus(result.reason?.includes('character') ? 'invalid' : 'taken');
      } catch { setSlugStatus(null); }
    }, 600);
  };

  const handleSave = async () => {
    // If slug is changing, show confirmation modal first
    const newSlug = omniUrl.trim();
    const isSlugChanging = newSlug && newSlug !== originalSlug;
    if (isSlugChanging && !showSlugModal) {
      if (slugStatus === 'taken') {
        setMessage({ type: 'error', text: 'That username is already taken.' });
        return;
      }
      if (slugLastChangedAt) {
        const daysSince = (Date.now() - new Date(slugLastChangedAt).getTime()) / (1000 * 60 * 60 * 24);
        if (daysSince < 30) {
          const daysLeft = Math.ceil(30 - daysSince);
          setMessage({ type: 'error', text: `Username can only be changed once every 30 days. ${daysLeft} day(s) remaining.` });
          return;
        }
      }
      setShowSlugModal(true);
      return;
    }
    setShowSlugModal(false);
    try {
      setLoading(true);
      setMessage({ type: '', text: '' });
      
      let logoUrl = formData.businessLogo;
      
      // Upload logo if a new file was selected
      if (logoFile) {
        try {
          const uploadResponse = await apiService.uploadLogo(logoFile);
          if (uploadResponse.ok && uploadResponse.logoUrl) {
            logoUrl = uploadResponse.logoUrl;
          }
        } catch (error) {
          console.error('Logo upload error:', error);
          setMessage({ type: 'error', text: 'Failed to upload logo' });
          setLoading(false);
          return;
        }
      }
      
      const cardData = {
        fullName: formData.fullName,
        jobTitle: formData.jobTitle,
        businessName: formData.businessName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        cellphone: formData.cellphone,
        whatsapp: formData.whatsapp,
        wechat: formData.wechat,
        website: formData.website,
        address: formData.address,
        templateStyle: formData.templateStyle,
        businessLogo: logoUrl,
        socialLinks: formData.socialLinks,
        public_url_slug: omniUrl.trim() || undefined,
      };
      
      console.log('Saving card data:', cardData);

      const response = await apiService.updateCard(cardData);
      
      if (response.ok) {
        // Clear logo file after successful save
        setLogoFile(null);
        // Reload card data to get updated info including logo URL
        await loadCard();
        setEditing(false);
        setMessage({ type: 'success', text: 'Card updated successfully!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        setMessage({ type: 'error', text: response.error || 'Failed to save card' });
      }
    } catch (error) {
      console.error('Error saving card:', error);
      setMessage({ type: 'error', text: 'Failed to save card. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getShareUrl = () => {
    if (!card) return '';
    return `${window.location.origin}/card/${card.public_url_slug || card.id}`;
  };

  const handleShare = async () => {
    const shareUrl = getShareUrl();
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${formData.fullName} - Digital Business Card`,
          text: `Check out my digital business card`,
          url: shareUrl,
        });
      } catch (error) {
        console.log('Error sharing:', error);
        // Fallback to clipboard
        navigator.clipboard.writeText(shareUrl);
        setMessage({ type: 'success', text: 'Card link copied to clipboard!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      setMessage({ type: 'success', text: 'Card link copied to clipboard!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const hasSocialLinks = formData.socialLinks && (
    formData.socialLinks.linkedin || formData.socialLinks.twitter ||
    formData.socialLinks.facebook || formData.socialLinks.instagram ||
    formData.socialLinks.github || formData.socialLinks.tiktok ||
    formData.socialLinks.whatsapp || formData.socialLinks.wechat
  );

  const displayName = formData.fullName ||
    (user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : user?.name || 'User');
  const displayRole = formData.jobTitle || user?.job_title || 'CEO';
  const displayCompany = formData.businessName || user?.company || 'Omni';
  const avatarUrl = user?.avatar_url;

  if (loading && !card) {
    return (
      <div className="mycard-container">
        <div className="loading-state">Loading card...</div>
      </div>
    );
  }

  return (
    <div className="mycard-container">
      <div className="mycard-content">

        {message.text && (
          <div className={`message ${message.type}`}>{message.text}</div>
        )}

        {!editing ? (
          <>
            {/* ── HERO PROFILE CARD (matches mobile ProfileCard.png design) ── */}
            <div className="omni-hero-wrapper">
              <div className="omni-hero-card" style={{ backgroundImage: "url('/ProfileCard.png')" }}>
                <img src="/icons/logoLight.png" alt="OMNI" className="omni-hero-logo" />
                <div className="omni-avatar-ring">
                  <div className="omni-avatar-inner">
                    {avatarUrl && !avatarError ? (
                      <img
                        src={avatarUrl}
                        alt="Avatar"
                        className="omni-avatar-img"
                        onError={() => setAvatarError(true)}
                      />
                    ) : (
                      <div className="omni-avatar-placeholder">
                        <FiUser size={50} color="#9AA3A8" />
                      </div>
                    )}
                  </div>
                </div>
                <div className="omni-hero-divider" />
                <div className="omni-hero-info">
                  <h2 className="omni-hero-name">{displayName}</h2>
                  <p className="omni-hero-role">{displayRole} @ {displayCompany}</p>
                </div>
              </div>
            </div>

            {/* ── DETAILS CARD (matches mobile detailsCard) ── */}
            <div className="omni-details-card">
              <div className="omni-details-sidebar" />

              {formData.phoneNumber && (
                <a href={`tel:${formData.phoneNumber}`} className="omni-detail-row">
                  <div className="omni-detail-icon"><FiPhone size={20} /></div>
                  <span className="omni-detail-text">{formData.phoneNumber}</span>
                </a>
              )}

              {formData.cellphone && (
                <a href={`tel:${formData.cellphone}`} className="omni-detail-row">
                  <div className="omni-detail-icon"><FiSmartphone size={20} /></div>
                  <span className="omni-detail-text">{formData.cellphone}</span>
                </a>
              )}

              {formData.whatsapp && (
                <a href={`https://wa.me/${formData.whatsapp.replace(/\D/g, '')}`} className="omni-detail-row" target="_blank" rel="noopener noreferrer">
                  <div className="omni-detail-icon"><img src="/icons/whatsapp.png" alt="WhatsApp" className="omni-detail-img" /></div>
                  <span className="omni-detail-text">{formData.whatsapp}</span>
                </a>
              )}

              {formData.wechat && (
                <div className="omni-detail-row">
                  <div className="omni-detail-icon"><img src="/icons/wechat.png" alt="WeChat" className="omni-detail-img" /></div>
                  <span className="omni-detail-text">{formData.wechat}</span>
                </div>
              )}

              {formData.businessName && (
                <div className="omni-detail-row">
                  <div className="omni-detail-icon"><FiBriefcase size={20} /></div>
                  <span className="omni-detail-text">{formData.businessName}</span>
                </div>
              )}

              {formData.email && (
                <a href={`mailto:${formData.email}`} className="omni-detail-row">
                  <div className="omni-detail-icon"><FiMail size={20} /></div>
                  <span className="omni-detail-text">{formData.email}</span>
                </a>
              )}

              {formData.website && (
                <a href={formData.website.startsWith('http') ? formData.website : `https://${formData.website}`} className="omni-detail-row" target="_blank" rel="noopener noreferrer">
                  <div className="omni-detail-icon"><FiGlobe size={20} /></div>
                  <span className="omni-detail-text">{formData.website}</span>
                </a>
              )}

              {formData.address && (
                <div className="omni-detail-row">
                  <div className="omni-detail-icon"><FiMapPin size={20} /></div>
                  <span className="omni-detail-text">{formData.address}</span>
                </div>
              )}

              {/* OMNI Profile Link */}
              <a href={`https://my-omni.link/${omniUrl || card?.public_url_slug || ''}`} className="omni-detail-row" target="_blank" rel="noopener noreferrer">
                <div className="omni-detail-icon">
                  <img src="/icons/logoLight.png" alt="OMNI" className="omni-detail-omni-icon" />
                </div>
                <span className="omni-detail-text">{omniUrl || card?.public_url_slug || 'your-profile'}</span>
              </a>

              {/* Social Media Icons */}
              {hasSocialLinks && (
                <div className="omni-social-section">
                  {formData.socialLinks.linkedin && (
                    <a href={formData.socialLinks.linkedin} className="omni-social-icon omni-social-linkedin" target="_blank" rel="noopener noreferrer">
                      <FaLinkedinIn size={20} />
                    </a>
                  )}
                  {formData.socialLinks.twitter && (
                    <a href={formData.socialLinks.twitter} className="omni-social-icon omni-social-twitter" target="_blank" rel="noopener noreferrer">
                      <span className="omni-x-logo">𝕏</span>
                    </a>
                  )}
                  {formData.socialLinks.facebook && (
                    <a href={formData.socialLinks.facebook} className="omni-social-icon omni-social-facebook" target="_blank" rel="noopener noreferrer">
                      <FaFacebookF size={20} />
                    </a>
                  )}
                  {formData.socialLinks.instagram && (
                    <a href={formData.socialLinks.instagram} className="omni-social-icon omni-social-instagram" target="_blank" rel="noopener noreferrer">
                      <FaInstagram size={20} />
                    </a>
                  )}
                  {formData.socialLinks.github && (
                    <a href={formData.socialLinks.github} className="omni-social-icon omni-social-github" target="_blank" rel="noopener noreferrer">
                      <FaGithub size={20} />
                    </a>
                  )}
                  {formData.socialLinks.whatsapp && (
                    <a href={`https://wa.me/${formData.socialLinks.whatsapp.replace(/\D/g, '')}`} className="omni-social-icon omni-social-whatsapp" target="_blank" rel="noopener noreferrer">
                      <img src="/icons/whatsapp.png" alt="WhatsApp" className="omni-social-img" />
                    </a>
                  )}
                  {formData.socialLinks.wechat && (
                    <div className="omni-social-icon omni-social-wechat">
                      <img src="/icons/wechat.png" alt="WeChat" className="omni-social-img" />
                    </div>
                  )}
                  {formData.socialLinks.tiktok && (
                    <a href={formData.socialLinks.tiktok} className="omni-social-icon omni-social-tiktok" target="_blank" rel="noopener noreferrer">
                      <img src="/icons/tiktok.png" alt="TikTok" className="omni-social-img" />
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* ── ACTION BUTTONS ── */}
            <div className="omni-action-row">
              <button className="omni-action-btn" onClick={handleShare}>
                <FiShare2 size={16} /> Share
              </button>
              <button className="omni-action-btn" onClick={() => setEditing(true)}>
                <FiEdit2 size={16} /> Edit
              </button>
              {card && (
                <button className="omni-action-btn" onClick={() => setShowQR(!showQR)}>
                  <FiGrid size={16} /> {showQR ? 'Hide QR' : 'QR Code'}
                </button>
              )}
            </div>

            {/* QR Section */}
            {showQR && card && (
              <div className="qr-section">
                <div className="qr-card">
                  <h3 className="qr-title">Share via QR Code</h3>
                  <div className="qr-code-wrapper">
                    <QRCodeSVG value={getShareUrl()} size={220} bgColor="#FFFFFF" fgColor="#0B0F0E" level="H" includeMargin={true} />
                  </div>
                  <p className="qr-hint">Scan to view this card</p>
                  <button className="btn btn-primary" onClick={handleShare}>
                    <FiShare2 size={18} /> Share Link
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          /* ── EDIT FORM ── */
          <div className="card">
            <div className="mycard-edit-header">
              <h3 className="section-title">Edit Card</h3>
              <div className="mycard-edit-actions">
                <button className="action-btn" onClick={() => { setEditing(false); loadCard(); }}>
                  <FiX size={18} /> Cancel
                </button>
                <button className="action-btn primary" onClick={handleSave} disabled={loading}>
                  <FiSave size={18} /> {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
            <div className="divider" />

            {/* Logo Upload */}
            <div className="logo-upload-section">
              <label className="form-label">Business Logo</label>
              <div className="logo-upload-container">
                {logoPreview ? (
                  <div className="logo-preview">
                    <img src={logoPreview} alt="Logo" />
                    <button type="button" className="logo-remove-btn" onClick={() => { setLogoFile(null); setLogoPreview(null); setFormData(prev => ({ ...prev, businessLogo: null })); }}>
                      <FiX size={16} />
                    </button>
                  </div>
                ) : (
                  <label className="logo-upload-btn">
                    <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
                    <FiUpload size={24} />
                    <span>Upload Logo</span>
                  </label>
                )}
              </div>
              <p className="logo-hint">Recommended: Square image, max 5MB</p>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input type="text" className="form-input" value={formData.fullName} onChange={(e) => handleInputChange('fullName', e.target.value)} placeholder="John Doe" />
              </div>
              <div className="form-group">
                <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>
                    <img src="/icons/logoLight.png" alt="OMNI" style={{ width: 14, height: 14, marginRight: 6, filter: 'brightness(0) invert(1)' }} />
                    Profile Username
                  </span>
                  <span style={{ fontSize: 13, color: '#9AA3A8', fontWeight: 400 }}>my-omni.link/<strong>{omniUrl || 'your-username'}</strong></span>
                </label>
                {slugLocked ? (
                  <div
                    className="slug-locked-field"
                    onClick={() => setShowCooldownPopup(true)}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px' }}
                    title={`Locked for ${slugDaysLeft} more day(s)`}
                  >
                    <span className="slug-locked-value">{omniUrl}</span>
                    <span style={{ fontSize: 12, color: '#F4A261', fontWeight: 400 }}>🔒 Locked — {slugDaysLeft} day(s) until you can change again</span>
                  </div>
                ) : (
                  <input
                    type="text"
                    className="form-input"
                    value={omniUrl}
                    onChange={(e) => handleSlugChange(e.target.value)}
                    placeholder="your-username"
                  />
                )}
                {!slugLocked && slugStatus === 'checking' && <p className="slug-hint slug-checking" style={{ color: '#9AA3A8', fontWeight: 500 }}>Checking availability…</p>}
                {!slugLocked && slugStatus === 'available' && <p className="slug-hint" style={{ color: '#4ECDC4', fontWeight: 500 }}><span className="slug-available-badge">✓ Available</span></p>}
                {!slugLocked && slugStatus === 'taken' && <p className="slug-hint slug-taken" style={{ color: '#FF6B6B', fontWeight: 500 }}>✗ Already taken — choose another</p>}
                {!slugLocked && slugStatus === 'invalid' && <p className="slug-hint slug-taken" style={{ color: '#FF6B6B', fontWeight: 500 }}>✗ 6-24 characters, letters and numbers only (no special characters or all numbers)</p>}
                {!slugLocked && slugStatus === null && <p className="slug-hint" style={{ color: '#9AA3A8', fontSize: 12 }}>Can only be changed once every 30 days</p>}
              </div>
              <div className="form-group">
                <label className="form-label">Job Title</label>
                <input type="text" className="form-input" value={formData.jobTitle} onChange={(e) => handleInputChange('jobTitle', e.target.value)} placeholder="CEO" />
              </div>
              <div className="form-group">
                <label className="form-label">Business Name</label>
                <input type="text" className="form-input" value={formData.businessName} onChange={(e) => handleInputChange('businessName', e.target.value)} placeholder="ACME Inc." />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input type="email" className="form-input" value={formData.email} onChange={(e) => handleInputChange('email', e.target.value)} placeholder="john@example.com" />
              </div>
              <div className="form-group">
                <label className="form-label">Phone Number</label>
                <input type="tel" className="form-input" value={formData.phoneNumber} onChange={(e) => handleInputChange('phoneNumber', e.target.value)} placeholder="+1 234 567 8900" />
              </div>
              <div className="form-group">
                <label className="form-label">Cellphone</label>
                <input type="tel" className="form-input" value={formData.cellphone} onChange={(e) => handleInputChange('cellphone', e.target.value)} placeholder="+1 234 567 8901" />
              </div>
              <div className="form-group">
                <label className="form-label">WhatsApp</label>
                <input type="tel" className="form-input" value={formData.whatsapp} onChange={(e) => handleInputChange('whatsapp', e.target.value)} placeholder="+1 234 567 8900" />
              </div>
              <div className="form-group">
                <label className="form-label">WeChat ID</label>
                <input type="text" className="form-input" value={formData.wechat} onChange={(e) => handleInputChange('wechat', e.target.value)} placeholder="WeChat ID" />
              </div>
              <div className="form-group">
                <label className="form-label">Website</label>
                <input type="url" className="form-input" value={formData.website} onChange={(e) => handleInputChange('website', e.target.value)} placeholder="https://example.com" />
              </div>
              <div className="form-group full-width">
                <label className="form-label">Address</label>
                <input type="text" className="form-input" value={formData.address} onChange={(e) => handleInputChange('address', e.target.value)} placeholder="123 Main St, City, Country" />
              </div>
            </div>

            {/* Social Media Links */}
            <div className="social-links-section">
              <h4 className="section-subtitle">Social Media Links</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label"><FiLinkedin size={14} /> LinkedIn</label>
                  <input type="url" className="form-input" value={formData.socialLinks.linkedin} onChange={(e) => setFormData(prev => ({ ...prev, socialLinks: { ...prev.socialLinks, linkedin: e.target.value } }))} placeholder="https://linkedin.com/in/username" />
                </div>
                <div className="form-group">
                  <label className="form-label"><FiTwitter size={14} /> X (Twitter)</label>
                  <input type="url" className="form-input" value={formData.socialLinks.twitter} onChange={(e) => setFormData(prev => ({ ...prev, socialLinks: { ...prev.socialLinks, twitter: e.target.value } }))} placeholder="https://x.com/username" />
                </div>
                <div className="form-group">
                  <label className="form-label"><FiFacebook size={14} /> Facebook</label>
                  <input type="url" className="form-input" value={formData.socialLinks.facebook} onChange={(e) => setFormData(prev => ({ ...prev, socialLinks: { ...prev.socialLinks, facebook: e.target.value } }))} placeholder="https://facebook.com/username" />
                </div>
                <div className="form-group">
                  <label className="form-label"><FiInstagram size={14} /> Instagram</label>
                  <input type="url" className="form-input" value={formData.socialLinks.instagram} onChange={(e) => setFormData(prev => ({ ...prev, socialLinks: { ...prev.socialLinks, instagram: e.target.value } }))} placeholder="https://instagram.com/username" />
                </div>
                <div className="form-group">
                  <label className="form-label"><FiGithub size={14} /> GitHub</label>
                  <input type="url" className="form-input" value={formData.socialLinks.github} onChange={(e) => setFormData(prev => ({ ...prev, socialLinks: { ...prev.socialLinks, github: e.target.value } }))} placeholder="https://github.com/username" />
                </div>
                <div className="form-group">
                  <label className="form-label">TikTok</label>
                  <input type="url" className="form-input" value={formData.socialLinks.tiktok} onChange={(e) => setFormData(prev => ({ ...prev, socialLinks: { ...prev.socialLinks, tiktok: e.target.value } }))} placeholder="https://tiktok.com/@username" />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <img src="/icons/whatsapp.png" alt="WhatsApp" style={{ width: 14, height: 14, marginRight: 4 }} /> WhatsApp Link
                  </label>
                  <input type="tel" className="form-input" value={formData.socialLinks.whatsapp} onChange={(e) => setFormData(prev => ({ ...prev, socialLinks: { ...prev.socialLinks, whatsapp: e.target.value } }))} placeholder="+1 234 567 8900" />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <img src="/icons/wechat.png" alt="WeChat" style={{ width: 14, height: 14, marginRight: 4 }} /> WeChat Social
                  </label>
                  <input type="text" className="form-input" value={formData.socialLinks.wechat} onChange={(e) => setFormData(prev => ({ ...prev, socialLinks: { ...prev.socialLinks, wechat: e.target.value } }))} placeholder="WeChat ID or link" />
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button className="btn btn-primary" onClick={handleSave} disabled={loading || slugStatus === 'taken' || slugStatus === 'checking'}>
                {loading ? 'Saving...' : 'Save Card'}
              </button>
            </div>
          </div>
        )}

        {/* Slug change confirmation modal */}
        {showSlugModal && (
          <div className="slug-modal-overlay" onClick={() => setShowSlugModal(false)}>
            <div className="slug-modal" onClick={(e) => e.stopPropagation()}>
              <div className="slug-modal-icon">🔗</div>
              <h3 className="slug-modal-title">Change Username?</h3>
              <p className="slug-modal-body">
                Your new profile URL will be:
              </p>
              <div className="slug-modal-url-box">
                https://my-omni.link/<strong>{omniUrl}</strong>
              </div>
              <p className="slug-modal-warning">
                ⚠️ You won't be able to change it again for <strong>30 days</strong>.
              </p>
              <div className="slug-modal-actions">
                <button className="btn btn-secondary" onClick={() => setShowSlugModal(false)}>Cancel</button>
                <button className="btn btn-danger" onClick={handleSave}>Confirm Change</button>
              </div>
            </div>
          </div>
        )}

        {/* Cooldown info popup */}
        {showCooldownPopup && (
          <div className="slug-modal-overlay" onClick={() => setShowCooldownPopup(false)}>
            <div className="slug-modal" onClick={(e) => e.stopPropagation()}>
              <div className="slug-modal-icon">🔒</div>
              <h3 className="slug-modal-title">Username Locked</h3>
              <p className="slug-modal-body">
                Your current username is:
              </p>
              <div className="slug-modal-url-box">
                https://my-omni.link/<strong>{omniUrl}</strong>
              </div>
              <div className="slug-cooldown-timer">
                <span className="slug-cooldown-number">{slugDaysLeft}</span>
                <span className="slug-cooldown-label">day{slugDaysLeft !== 1 ? 's' : ''} remaining</span>
              </div>
              <p className="slug-modal-warning">
                You can change your username again in <strong>{slugDaysLeft} day{slugDaysLeft !== 1 ? 's' : ''}</strong>.
              </p>
              <div className="slug-modal-actions">
                <button className="btn btn-primary" onClick={() => setShowCooldownPopup(false)}>Got it</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyCard;
